import React, { FC, useState } from 'react'
import {
  TextField,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Chip,
  Button
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { ConfigFieldProps } from '../../types/config'
import { getSourceDescription } from '../../services/configApi'
import useAccount from '../../hooks/useAccount'

const INHERITED_CLAMP_LINES = 6
const INHERITED_CLAMP_CHARS = 600

/**
 * ConfigField - Individual form field with inheritance display
 * 
 * Shows either the actual value for the entity or a placeholder for inherited values.
 * Includes info button for inherited values to show source information.
 */
const ConfigField: FC<ConfigFieldProps> = ({
  fieldName,
  label,
  value,
  strategy,
  sources,
  onChange,
  onShowSourceInfo,
  multiline = false,
  rows = 1,
  placeholder,
  required = false,
  disabled = false,
  readOnly = false,
  onBlur
}) => {
  const account = useAccount()
  const displayNames = {
    customerDisplayName: account.customer?.displayName,
    jobDisplayName: account.job?.displayName
  }

  const displayValue = value || ''
  const isMerge = strategy === 'merge'
  const localSource = sources.find(source => source.isLocal)
  const ancestorSources = sources.filter(source => !source.isLocal)
  const displaySources = isMerge ? ancestorSources : sources
  const inheritedSource = !localSource ? ancestorSources[ancestorSources.length - 1] : undefined
  const [expandedSources, setExpandedSources] = useState<string[]>([])
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  // The inherited text is plain, selectable text with a Copy button, so it
  // can be reused below (#1478: it used to sit inside a clickable accordion
  // header, where the browser will not select text).
  const handleCopy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKey(key)
      setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1500)
    } catch {
      // Clipboard refused (permissions); the text is still selectable.
    }
  }

  const formatValue = (input: unknown) => {
    if (typeof input === 'string') {
      return input
    }
    if (input === null || input === undefined) {
      return ''
    }
    if (typeof input === 'object') {
      try {
        return JSON.stringify(input, null, 2)
      } catch (_err) {
        return String(input)
      }
    }
    return String(input)
  }

  const inheritedPlaceholder = inheritedSource 
    ? `Inherited: ${formatValue(inheritedSource.reference.value)}`
    : placeholder

  const getInfoTooltip = (referenceLabel: string) => {
    return `This value comes from: ${referenceLabel}`
  }

  return (
    <Box sx={{ mb: 3, position: 'relative' }}>
      {isMerge && displaySources.length > 0 && (
        <Box
          sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}
          data-testid="inherited-sources"
        >
          {displaySources.map((sourceInfo, index) => {
            const reference = sourceInfo.reference
            const sourceLabel = sourceInfo.isLocal ? 'This entity' : getSourceDescription(reference, displayNames)
            const text = formatValue(reference.value).trim()
            const long = text.length > INHERITED_CLAMP_CHARS || text.split('\n').length > INHERITED_CLAMP_LINES
            const key = `${reference.source_config?.id || reference.field_name}-${index}`
            const expanded = expandedSources.includes(key)
            return (
              <Box
                key={key}
                sx={{ px: 2, py: 1.5, borderTop: index > 0 ? '1px solid' : 'none', borderColor: 'divider' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    From {sourceLabel}, used with the text below
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {text && (
                    <Button
                      size="small"
                      color="info"
                      startIcon={copiedKey === key ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                      onClick={() => handleCopy(key, text)}
                      aria-label={`Copy the ${label} from ${sourceLabel}`}
                    >
                      {copiedKey === key ? 'Copied' : 'Copy'}
                    </Button>
                  )}
                  {onShowSourceInfo && (
                    <Tooltip title={getInfoTooltip(sourceLabel)} arrow>
                      <IconButton
                        onClick={(event) => onShowSourceInfo(reference, event)}
                        size="small"
                        sx={{ color: 'text.secondary' }}
                        aria-label={`Where this comes from: ${sourceLabel}`}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    userSelect: 'text',
                    ...(long && !expanded
                      ? { display: '-webkit-box', WebkitLineClamp: INHERITED_CLAMP_LINES, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
                      : {})
                  }}
                >
                  {text || 'Nothing set here yet.'}
                </Typography>
                {long && (
                  <Button
                    size="small"
                    color="info"
                    onClick={() => setExpandedSources((keys) => (expanded ? keys.filter((k) => k !== key) : [...keys, key]))}
                    sx={{ mt: 0.5, ml: -1 }}
                  >
                    {expanded ? 'Show less' : 'Show all'}
                  </Button>
                )}
              </Box>
            )
          })}
        </Box>
      )}

      <TextField
        fullWidth
        label={label}
        value={displayValue}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={isMerge ? placeholder : inheritedPlaceholder}
        multiline={multiline}
        minRows={multiline ? rows : undefined}
        maxRows={multiline ? Math.max(rows, 20) : undefined}
        variant="outlined"
        required={required}
        disabled={readOnly || disabled}
        InputLabelProps={{
          shrink: true
        }}
        sx={{
          '& .MuiInputBase-input::placeholder': {
            color: !isMerge && inheritedSource ? 'text.secondary' : 'text.disabled',
            opacity: !isMerge && inheritedSource ? 0.8 : 0.42,
            fontStyle: !isMerge && inheritedSource ? 'italic' : 'normal'
          }
        }}
      />

      {!isMerge && inheritedSource && !displayValue && onShowSourceInfo && (
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          <Tooltip title={getInfoTooltip(getSourceDescription(inheritedSource.reference, displayNames))} arrow>
            <IconButton
              onClick={(event) => onShowSourceInfo(inheritedSource.reference, event)}
              size="small"
              sx={{ 
                color: 'success.main',
                backgroundColor: 'success.light',
                '&:hover': {
                  backgroundColor: 'success.main',
                  color: 'success.contrastText'
                }
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {!isMerge && inheritedSource && !displayValue && (
        <Box sx={{ mt: 1 }}>
          <Chip
            label={`Inherited from ${getSourceDescription(inheritedSource.reference, displayNames)}`}
            size="small"
            color="success"
            variant="outlined"
            sx={{
              fontSize: '0.75rem',
              height: '20px'
            }}
          />
        </Box>
      )}
    </Box>
  )
}

export default ConfigField
